import { datadogRum } from '@datadog/browser-rum';
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import {
  CropDialog,
  NotificationTypeEnum,
  useConcurrentialAccess,
  useDialog,
  useNotification,
} from '@prismamedia/one-components';
import 'cropperjs/dist/cropper.css';
import { format, isBefore } from 'date-fns';
import { isEmpty } from 'lodash';
import { findIndex } from 'ramda';
import {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from 'react';
import { RouteComponentProps, generatePath } from 'react-router-dom';
import {
  ArticleFormat,
  ArticleStatus,
  BrandKey,
  GetArticleQuery_query_article_Article,
} from '../../__generated__/queries-web';
import { useArticleUpsert } from '../../apollo/mutations/article.web.graphql';
import {
  useArticleGetter,
  useGetArticle,
} from '../../apollo/queries/articles.web.graphql';
import { paths } from '../../routing/Router/paths';
import { DATADOG_ACTIONS } from '../../tracking/actions';
import { MediaBlock } from '../../types/draft';
import { assert } from '../../utils/assert';
import { auth } from '../../utils/auth';
import { parseDraft } from '../../utils/draft';
import { standardizeArticleUrl } from '../../utils/url';
import { AppBar } from './AppBar';
import { DrawerContent } from './DrawerContent';
import {
  DEFAULT_SIDEBAR_WIDTH,
  ResizableSidebar,
  SIDEBAR_WIDTH_BOUNDS,
} from './ResizableSidebar';
import { Grid } from './Grid';
import { UploadButton } from './UploadButton';
import { computeUpsertArticleVariables } from './computeUpsertArticleVariables';
import { actions, initialState, reducer } from './reducer';
import { useStyles } from './styles';
import { useArticleConcurrentialAccess } from '../ArticleEdit/utils/articleConcurrentialAccess';
import { ConfirmDialog } from '../ArticleEdit/StatusTransition/ConfirmDialog';

const LAYOUT_OFFSET = { appBar: 70, selectionBar: 48 } as const;

interface SlideshowEditProps {
  articleData: GetArticleQuery_query_article_Article;
  afterSave?: (id: string) => void;
  history?: RouteComponentProps['history'];
}

const SlideshowEdit: FC<SlideshowEditProps> = ({
  articleData,
  afterSave,
  history,
}) => {
  const getArticle = useArticleGetter();
  const [upsert] = useArticleUpsert();
  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    article: articleData,
  });
  const { openDialog, closeDialog } = useDialog();
  const { pushNotification } = useNotification();
  const { lockArticle, retainArticleLock } = useArticleConcurrentialAccess();

  const { article, selectedSlidesIndex, slideMap, slidesIndex } = state;
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const stored = localStorage.getItem('SlideshowEdit_sidebarWidth');
    const n = stored ? Number(stored) : DEFAULT_SIDEBAR_WIDTH;
    const { min, max } = SIDEBAR_WIDTH_BOUNDS;
    return Number.isFinite(n) && n >= min && n <= max
      ? n
      : DEFAULT_SIDEBAR_WIDTH;
  });
  const handleSidebarWidthChange = useCallback((width: number) => {
    setSidebarWidth(width);
    localStorage.setItem('SlideshowEdit_sidebarWidth', String(width));
  }, []);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const body =
    article && article.body && typeof article.body === 'object'
      ? JSON.stringify(article?.body)
      : article?.body;
  const lockedBy = articleData?.lockedBy || auth.user;

  useConcurrentialAccess({
    lockStatus: {
      lockedAt: articleData?.lockedAt,
      lockedBy,
    },
    unlockTimeout: 600000,
    currentUser: auth.user,
    onLock: (firstLock) =>
      articleData.id
        ? firstLock
          ? lockArticle(articleData.id)
          : retainArticleLock(articleData.id)
        : Promise.resolve(),
    onNavigateBack: async () => {
      history?.push(paths.SLIDESHOW_LIST);
    },
    disableLockForV0: true,
  });

  const getImagesWithoutCredit = (draft: MediaBlock[]): number[] => {
    return draft
      .map(({ data, key }: MediaBlock): string | boolean =>
        !data.credit && !data.iframely.meta.credit ? key : false,
      )
      .filter(Boolean)
      .map((key: string | boolean): number =>
        findIndex((slideIndex) => key === slideIndex, slidesIndex),
      );
  };

  const imagesWithoutCredit = getImagesWithoutCredit(Object.values(slideMap));
  useEffect(() => {
    if (!body) {
      return;
    }
    const draft = parseDraft(body);
    dispatch(
      actions.setSlides({
        slides: draft.blocks.filter(
          (b: any) => b.type === 'atomic',
        ) as MediaBlock[],
      }),
    );
  }, [body, dispatch]);

  const validateSlideShow = useCallback(
    (status: ArticleStatus, hasCredit: boolean) => {
      if (
        [ArticleStatus.Published, ArticleStatus.Scheduled].includes(status) &&
        !hasCredit
      ) {
        const errorMessage =
          'Le champ crédit doit être renseigné pour les images suivantes :';

        imagesWithoutCredit.length > 0 &&
          openDialog(
            <>
              <DialogTitle>{errorMessage}</DialogTitle>
              <DialogContent>
                {imagesWithoutCredit.map((item, index) => (
                  <span key={item}>
                    {index === imagesWithoutCredit.length - 1
                      ? `${item + 1}`
                      : ` ${item + 1} , `}
                  </span>
                ))}
              </DialogContent>
              <DialogActions>
                <Button onClick={closeDialog} color="primary">
                  OK
                </Button>
              </DialogActions>
            </>,
          );
        return false;
      }
      return true;
    },
    [openDialog, closeDialog, imagesWithoutCredit],
  );

  const saveSlideshow = useCallback(
    async (forcedArticle?: GetArticleQuery_query_article_Article) => {
      const articleToSave = forcedArticle || article;
      if (!articleToSave) {
        return;
      }
      if (
        !validateSlideShow(
          articleToSave.status,
          imagesWithoutCredit.length === 0,
        )
      ) {
        return;
      }

      let serverVersionArticle = articleToSave;

      if (
        articleToSave.status === ArticleStatus.Published &&
        !articleToSave.publishedAt
      ) {
        // eslint-disable-next-line immutable/no-mutation
        articleToSave.publishedAt = format(
          Date.now(),
          "yyyy-MM-dd'T'HH:mm:ss.SSS",
        );
      }

      if (articleToSave.id) {
        const { data } = await getArticle(articleToSave.id);
        if (data.article) {
          serverVersionArticle = data.article;
        }
      }
      const variables = computeUpsertArticleVariables(
        serverVersionArticle,
        articleToSave,
        slideMap,
        slidesIndex,
      );

      try {
        if (!articleToSave.title) {
          throw new Error('Un titre est requis pour enregistrer ce diapo');
        }

        datadogRum.addAction(DATADOG_ACTIONS.SAVE_DIAPO, {
          articleDiapo: articleToSave,
        });

        const result = await upsert({ variables });

        if (!result || !result.data || !result.data.upsertArticle) {
          console.error('error', result);
          pushNotification({
            type: NotificationTypeEnum.error,
            message: "Erreur lors de l'enregistrement",
          });
          return;
        }

        if (afterSave) {
          afterSave(result.data.upsertArticle.id);
        }
        pushNotification({
          type: NotificationTypeEnum.success,
          message: 'Diaporama enregistré',
        });
      } catch (e) {
        pushNotification({
          type: NotificationTypeEnum.error,
          message:
            e instanceof Error ? e.message : "Erreur lors de l'enregistrement",
        });
      }
    },
    [
      article,
      slidesIndex,
      slideMap,
      upsert,
      afterSave,
      pushNotification,
      imagesWithoutCredit,
      getArticle,
      validateSlideShow,
    ],
  );

  useEffect(() => {
    const handleEvent = (e: KeyboardEvent) => {
      if (e.key === 's' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        saveSlideshow();
      }
    };

    window.addEventListener('keydown', handleEvent);

    return () => {
      window.removeEventListener('keydown', handleEvent);
    };
  }, [saveSlideshow]);

  const selectedSlides = useMemo(
    () => selectedSlidesIndex.map((key) => slideMap[key]),
    [selectedSlidesIndex, slideMap],
  );

  const selectedSlideMeta = assert(
    selectedSlides,
    (slides) => slides[0].data.iframely.meta,
  );

  const getSlidesCaptions = () => {
    if (!isEmpty(slideMap)) {
      return Object.values(slideMap).map(({ data }) => data.caption);
    }
  };

  const selectedFocusPoint = selectedSlides[0]?.data.focusPoint;

  if (!articleData) {
    return <Typography>ERROR</Typography>;
  }
  const id = (article && article.id) || undefined;
  const title = (article && article.title) || '';
  const brandKey = (article && article.brandKey) || null;
  const publishedAt =
    (article &&
      article.publishedAt &&
      format(new Date(article.publishedAt), "yyyy-MM-dd'T'HH:mm")) ||
    '';
  const categoryId =
    (article &&
      article.categories.length > 0 &&
      article.categories[0].category.id) ||
    '';
  const status = article && (article.status as ArticleStatus);
  const publicUrl = articleData && standardizeArticleUrl(articleData.canonical);

  const updateSlideshow = async (
    newStatus: ArticleStatus,
    publishedAtDate?: Date,
    editedAt?: string,
  ) => {
    if (!validateSlideShow(newStatus, imagesWithoutCredit.length === 0)) {
      return;
    }
    await saveSlideshow({
      ...article,
      status: newStatus,
      ...(publishedAtDate && { publishedAt: publishedAtDate.toISOString() }),
      ...(editedAt && { editedAt }),
    } as GetArticleQuery_query_article_Article);

    // Update local state with the new status
    dispatch(actions.setArticleStatus({ status: newStatus }));

    // Update local state with the new publishedAt if provided
    if (publishedAtDate) {
      dispatch(
        actions.setPublishedAt({
          publishedAt: publishedAtDate.toISOString(),
        }),
      );
    }

    closeDialog();
  };

  const changeStatus = async (
    newStatus: ArticleStatus,
    transitionType?: string,
  ) => {
    switch (newStatus) {
      case ArticleStatus.Published:
        if (status === ArticleStatus.Draft || !publishedAt) {
          updateSlideshow(ArticleStatus.Published, new Date());
          return;
        }

        if (status === ArticleStatus.Published) {
          if (transitionType === 'antidate') {
            openDialog(
              <ConfirmDialog
                title="Antidater l'article"
                message="Confirmez-vous d'antidater le"
                enableDateInput
                dateInit={publishedAt ? new Date(publishedAt) : new Date()}
                onValidate={(date) =>
                  updateSlideshow(
                    ArticleStatus.Published,
                    date,
                    date.toISOString(),
                  )
                }
                onCancel={closeDialog}
              />,
              { disableClose: true },
            );
            return;
          }

          openDialog(
            <ConfirmDialog
              title="Republier l'article"
              message="Confirmez-vous la republication ?"
              onValidate={() =>
                updateSlideshow(
                  ArticleStatus.Published,
                  new Date(),
                  new Date().toISOString(),
                )
              }
              onCancel={closeDialog}
            />,
            { disableClose: true },
          );
          return;
        }

        openDialog(
          <ConfirmDialog
            title="Confirmation de publication"
            message={`Cet article a une date de programmation définie pour le ${format(
              new Date(publishedAt),
              'PPPppp',
            )}.\n Souhaitez-vous tout de même le publier immédiatement ?`}
            onValidate={() =>
              updateSlideshow(
                ArticleStatus.Published,
                new Date(),
                new Date().toISOString(),
              )
            }
            onCancel={closeDialog}
          />,
          { disableClose: true },
        );
        break;

      case ArticleStatus.Scheduled:
        openDialog(
          <ConfirmDialog
            title={
              publishedAt && isBefore(new Date(publishedAt), new Date())
                ? "Antidater l'article"
                : "Programmer l'article"
            }
            message="Confirmez-vous la programmation le"
            enableDateInput
            dateInit={publishedAt ? new Date(publishedAt) : new Date()}
            onValidate={(date) =>
              updateSlideshow(ArticleStatus.Scheduled, date)
            }
            onCancel={closeDialog}
          />,
          { disableClose: true },
        );
        break;

      case ArticleStatus.ScheduledForRepublication:
        openDialog(
          <ConfirmDialog
            title="Programmer la republication de l'article"
            message="Confirmez-vous la programmation le"
            enableDateInput
            dateInit={new Date()}
            onValidate={(date) =>
              updateSlideshow(ArticleStatus.ScheduledForRepublication, date)
            }
            onCancel={closeDialog}
          />,
          { disableClose: true },
        );
        break;

      case ArticleStatus.Draft:
        if (
          status === ArticleStatus.Published &&
          (transitionType === 'unpublish' || transitionType === 'back2draft')
        ) {
          openDialog(
            <ConfirmDialog
              title="Dépublier un article"
              message="⚠️ Attention : Êtes-vous sûr(e) de vouloir dépublier cet article ?"
              onValidate={() =>
                updateSlideshow(ArticleStatus.Draft, new Date())
              }
              onCancel={closeDialog}
            />,
            { disableClose: true },
          );
          return;
        }
        await updateSlideshow(newStatus);
        break;
      default:
        await updateSlideshow(newStatus);
    }
  };

  return (
    <>
      <AppBar
        id={id}
        brandKey={brandKey}
        slidesIndex={slidesIndex}
        selectedSlidesIndex={selectedSlidesIndex}
        title={title}
        format={article?.format}
        status={status}
        changeStatus={changeStatus}
        saveSlideshow={saveSlideshow}
        dispatch={dispatch}
        publicUrl={publicUrl}
        mediaCanonicalUrl={articleData?.canonical ?? publicUrl}
        slideMap={slideMap}
        onSidebarToggle={() => setSidebarVisible((v) => !v)}
        openCropModal={() => {
          openDialog(
            <CropDialog
              closeDialog={closeDialog}
              imageSrc={selectedSlideMeta.url}
              config={{
                height: selectedSlideMeta.height,
                width: selectedSlideMeta.width,
              }}
              initialCrop={assert(
                selectedSlides,
                (slides) => slides[0].data.crop,
              )}
              initialFocusPoint={selectedFocusPoint}
              onValidate={({ crop, focusPoint }) => {
                dispatch(
                  actions.setSlideCrop({
                    slideKey: selectedSlides[0].key,
                    crop,
                    focusPoint,
                  }),
                );
              }}
            />,
            { fullScreen: true },
          );
        }}
        articleHistories={articleData.histories}
        concurrentialAccess={{
          lockStatus: {
            lockedBy,
          },
          disableLockForV0: true,
        }}
      />

      <Box
        sx={{
          display: 'flex',
          marginTop: `${
            LAYOUT_OFFSET.appBar +
            (selectedSlides.length ? LAYOUT_OFFSET.selectionBar : 0)
          }px`,
          height: `calc(100vh - ${
            LAYOUT_OFFSET.appBar +
            (selectedSlides.length ? LAYOUT_OFFSET.selectionBar : 0)
          }px)`,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            borderRight: 'none',
            position: 'relative',
            zIndex: 0,
            '& [class*="wrapper"], & [class*="listWrapper"]': {
              borderRight: 'none',
            },
          }}
        >
          <Grid
            dispatch={dispatch}
            selectedSlidesIndex={selectedSlidesIndex}
            slidesIndex={slidesIndex}
            slideMap={slideMap}
            brandKey={brandKey}
          />
          <UploadButton
            brandKey={brandKey}
            articleStatus={status}
            dispatch={dispatch}
            captions={getSlidesCaptions()}
          />
        </Box>
        {article && sidebarVisible && (
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              width: sidebarWidth,
              flexShrink: 0,
              overflow: 'visible',
              backgroundColor: 'background.paper',
              pr: 2,
              pl: 0,
            }}
          >
            <ResizableSidebar
              width={sidebarWidth}
              onWidthChange={handleSidebarWidthChange}
            >
              <DrawerContent
                categoryId={categoryId}
                dispatch={dispatch}
                publishedAt={publishedAt}
                selectedSlides={selectedSlides}
                article={article}
              />
            </ResizableSidebar>
          </Box>
        )}
      </Box>
    </>
  );
};

export const EditSlideshowWrapper: FC<RouteComponentProps<{
  id: string;
}>> = ({ match, history }) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { loading, data } = useGetArticle(match.params.id!);
  const classes = useStyles();

  if (loading) {
    return (
      <div className={classes.centered}>
        <CircularProgress size={80} />
      </div>
    );
  }

  if (!data || !data.article) {
    return <Typography>ERROR</Typography>;
  }

  return <SlideshowEdit articleData={data.article} history={history} />;
};

export const NewSlideshowWrapper: FC<RouteComponentProps<{
  brandKey: string;
}>> = ({ match, history }) => {
  const article: GetArticleQuery_query_article_Article = {
    __typename: 'Article',
    id: '',
    title: '',
    body: '{ "blocks": [], "entityMap": [] }',
    brandKey: match.params.brandKey as BrandKey,
    publishedAt: null,
    createdAt: '',
    updatedAt: '',
    format: ArticleFormat.Slideshow,
    status: ArticleStatus.Draft,
    lockedBy: null,
    histories: [],
    categories: [],
    tags: [],
    urls: [],
    canonical: null,
    authors: [],
    channels: [],
    events: [],
    guides: [],
    machineTags: [],
    qualifiers: [],
    bibliographies: [],
    editedAt: null,
    lead: null,
    live: null,
    medias: null,
    partnerTitle: null,
    discoverTitle: null,
    pushCount: 0,
    secondaryTitle: null,
    slug: '',
    source: null,
    platformId: null,
  };
  const afterSave = (id: string) => {
    history.push(generatePath(paths.SLIDESHOW_EDIT, { id }));
  };
  return <SlideshowEdit articleData={article} afterSave={afterSave} />;
};

export const DuplicateSlideshowWrapper: FC<RouteComponentProps<{
  brandKey: BrandKey;
  id: string;
}>> = ({ match, history }) => {
  const { data, loading } = useGetArticle(match.params.id);
  const classes = useStyles();

  if (loading) {
    return (
      <div className={classes.centered}>
        <CircularProgress size={80} />
      </div>
    );
  }

  if (!data || !data.article) {
    return <Typography>ERROR</Typography>;
  }

  const duplicate = {
    ...data.article,
    status: ArticleStatus.Draft,
    brandKey: match.params.brandKey,
    id: '',
  };

  const afterSave = (id: string) => {
    history.push(generatePath(paths.SLIDESHOW_EDIT, { id }));
  };

  return <SlideshowEdit articleData={duplicate} afterSave={afterSave} />;
};
